import React, { useCallback, useState } from 'react';

import { FlatList, ListRenderItemInfo, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { RightArrowSettings } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    RESET_MY_LIST_CANCEL_BUTTON,
    RESET_MY_LIST_DESCRIPTION,
    RESET_MY_LIST_RESET_BUTTON,
    RESET_MY_LIST_TITLE,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { resetFavouritesChannelsList } from 'store/user/actions';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import { isTabletOrWeb, isWeb } from 'utils/responsiveness';

import SettingsRowItem from 'screens/Settings/components/RowItem';
import WebHeader from 'screens/Settings/components/WebHeader';
import useChannelsLineups from 'screens/Settings/hooks/useChannelsLineups';
import {
    FTI_CHANNELS_MY_LIST_TITLE,
    RESET_BUTTON,
    SETTINGS_CHANNEL_LINEUP_CLEAR_MY_LIST_DESCRIPTION,
    SETTINGS_CHANNEL_LINEUP_CLEAR_MY_LIST_TITLE,
} from 'screens/Settings/languagesConst';
import { ChannelsLineup } from 'screens/Settings/types';

import DialogView from 'components/Dialog';
import { ETextColor } from 'components/Text';

import styles from '../styles';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.MY_LIST>;
};

const { CANCEL_BUTTON } = LANGUAGE_KEYS;

function MyList({ navigation }: Props) {
    const { t } = useTranslation();

    useHeaderConfig({ isRoot: isWeb, disableFocusOnMount: true });

    const { background, primary } = useSelector(selectLayoutConfig);
    const { myListOptions, myList } = useChannelsLineups();
    const dispatch = useDispatch();

    const [showResetDialog, setShowResetDialog] = useState(false);

    const handlePress = useCallback(
        (item: ChannelsLineup) => {
            if (item.screen) {
                navigation.navigate(item.screen);
            } else {
                setShowResetDialog(true);
            }
        },
        [navigation],
    );

    const handleCancelButtonPress = useCallback(() => {
        setShowResetDialog(false);

        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            navigation.reset({
                routes: [{ name: SCREEN.SWITCH_CHANNEL_LIST }],
            });
        }
    }, [navigation]);

    const handleResetButtonPress = useCallback(() => {
        dispatch(
            resetFavouritesChannelsList(myList.id, {
                onSuccess: handleCancelButtonPress,
            }),
        );
    }, [myList]);

    const renderItem = useCallback(
        ({ item, index }: ListRenderItemInfo<ChannelsLineup>) => (
            <SettingsRowItem
                item={item}
                title={t(item.title)}
                onPress={handlePress}
                RightIcon={RightArrowSettings}
                titleColor={ETextColor.neutral}
                position={index + 1}
                total={myListOptions.length}
            />
        ),
        [t, handlePress],
    );

    return (
        <>
            {isWeb && (
                <WebHeader
                    title={t(FTI_CHANNELS_MY_LIST_TITLE)}
                    disableFocusOnMount
                />
            )}
            <View
                style={[
                    gStyles.settingsContainer,
                    { backgroundColor: background },
                ]}
            >
                <FlatList
                    data={myListOptions}
                    keyExtractor={keyExtractor}
                    style={[
                        gStyles.settingsContentContainer,
                        { backgroundColor: background },
                    ]}
                    snapToAlignment="start"
                    decelerationRate="fast"
                    renderItem={renderItem}
                    showsHorizontalScrollIndicator={false}
                    keyboardDismissMode="on-drag"
                    overScrollMode="never"
                    bounces={false}
                    accessibilityRole="menu"
                />
            </View>

            <DialogView
                visible={showResetDialog}
                style={[styles.dialog, { backgroundColor: background }]}
                title={SETTINGS_CHANNEL_LINEUP_CLEAR_MY_LIST_TITLE}
                titleTestID={RESET_MY_LIST_TITLE}
                description={SETTINGS_CHANNEL_LINEUP_CLEAR_MY_LIST_DESCRIPTION}
                descriptionTestID={RESET_MY_LIST_DESCRIPTION}
                descriptionNumberOfLines={isTabletOrWeb ? 6 : 8}
                actionButtonTitle={CANCEL_BUTTON}
                actionButtonAccessibilityLabel={CANCEL_BUTTON}
                actionButtonFillColor={background}
                cancelButtonStyle={styles.button}
                cancelButtonTextStyle={styles.buttonText}
                actionButtonTestID={RESET_MY_LIST_CANCEL_BUTTON}
                cancelButtonTitle={RESET_BUTTON}
                cancelButtonAccessibilityLabel={RESET_BUTTON}
                cancelButtonFillColor={primary}
                closeButtonTestID={RESET_MY_LIST_RESET_BUTTON}
                actionButtonOnPress={handleCancelButtonPress}
                cancelButtonOnPress={handleResetButtonPress}
                actionButtonBordered
                cancelButtonBordered
                onRequestClose={handleCancelButtonPress}
            />
        </>
    );
}

export default MyList;

function keyExtractor(item: { id: string }) {
    return item.id;
}
